import { useState, useCallback } from 'react';
import { GovernanceTask, Tool, CriticalityLevel, DEFAULT_CRITICALITY_LEVELS } from '../types';
import { getGovernanceTasks, addGovernanceTask, toggleGovernanceTask as apiToggle, deleteGovernanceTask as apiDelete } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

function getPriorityForTool(tool: Tool, criticalityLevels: CriticalityLevel[]): string {
  const level = criticalityLevels.find(l => l.id === tool.importance);
  return level?.governancePriority ?? 'MEDIUM';
}

function addMonths(date: Date, months: number): string {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function useGovernance(companyId: string | null) {
  const [tasks, setTasks] = useState<GovernanceTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const data = await runAction((cid) => getGovernanceTasks(cid));
    const safe = Array.isArray(data) ? data : [];
    setTasks(safe);
    return safe;
  }, [runAction]);

  const add = useCallback(async (task: GovernanceTask) => {
    const updated = await runAction((cid) => addGovernanceTask(task, cid));
    const safe = Array.isArray(updated) ? updated : [];
    setTasks(safe);
    return safe;
  }, [runAction]);

  const syncFromTools = useCallback(async (toolsList: Tool[], existingTasks: GovernanceTask[], criticalityLevels?: CriticalityLevel[]): Promise<GovernanceTask[]> => {
    const levels = criticalityLevels?.length ? criticalityLevels : DEFAULT_CRITICALITY_LEVELS;
    const existing = Array.isArray(existingTasks) ? existingTasks : [];
    const toAdd: GovernanceTask[] = [];
    for (const tool of toolsList || []) {
      const priority = getPriorityForTool(tool, levels);

      if (tool.renewalDate) {
        const exists = existing.some(
          t => t.toolId === tool.id && t.dueDate === tool.renewalDate && t.title?.startsWith('Renewal: ')
        );
        if (!exists) toAdd.push({
          id: '', title: `Renewal: ${tool.name}`, toolId: tool.id,
          dueDate: tool.renewalDate, status: 'PENDING', priority, isCompleted: false,
        });
      }
      if (tool.noticeDeadline) {
        const exists = existing.some(
          t => t.toolId === tool.id && t.dueDate === tool.noticeDeadline && t.title?.startsWith('Notice deadline: ')
        );
        if (!exists) toAdd.push({
          id: '', title: `Notice deadline: ${tool.name}`, toolId: tool.id,
          dueDate: tool.noticeDeadline, status: 'PENDING', priority, isCompleted: false,
        });
      }

      const level = levels.find(l => l.id === tool.importance);
      if (level) {
        const hasPendingReview = existing.some(
          t => t.toolId === tool.id && t.title?.startsWith('Review: ') && !t.isCompleted
        );
        if (!hasPendingReview) {
          const dueDate = addMonths(new Date(), level.reviewFrequencyMonths);
          toAdd.push({
            id: '', title: `Review: ${tool.name}`, toolId: tool.id,
            dueDate, status: 'PENDING', priority, isCompleted: false,
          });
        }
      }
    }
    const result = await runAction(async (cid) => {
      for (const task of toAdd) {
        await addGovernanceTask(task, cid);
      }
      return toAdd.length > 0 ? getGovernanceTasks(cid) : Promise.resolve(existing);
    });
    const safe = Array.isArray(result) ? result : [];
    setTasks(safe);
    return safe;
  }, [runAction]);

  const toggle = useCallback(async (id: string) => {
    const updated = await runAction((cid) => apiToggle(id, cid));
    const safe = Array.isArray(updated) ? updated : [];
    setTasks(safe);
    return safe;
  }, [runAction]);

  const remove = useCallback(async (id: string) => {
    const updated = await runAction((cid) => apiDelete(id, cid));
    const safe = Array.isArray(updated) ? updated : [];
    setTasks(safe);
    return safe;
  }, [runAction]);

  return { tasks, setTasks, loading, error, load, add, toggle, remove, syncFromTools };
}
